"use client"

import type { ComponentProps, FormEventHandler, FunctionComponent, KeyboardEvent } from "react"

import { useSearchParams } from "next/navigation"
import { useRef } from "react"

import { Search } from "@jobteaser/spark/components/Icons/Search"
import { MultiSelect } from "@jobteaser/spark/components/MultiSelect"
import { TextInput } from "@jobteaser/spark/components/TextInput"

import { useJobRolesFilters } from "@/features/jobRoles/hooks/useJobRolesFilters"
import { useLayoutContext } from "@/modules/landingSearch/components/Layout/LayoutContextProvider"

import styles from "@/modules/landingSearch/components/Filters/Filters.module.css"

type FiltersProps = {
  clearIconAriaLabel: string
  label: string
  multipleSelectedLabel: string
  positionCategoryIdsLabel: string
  positionCategories: ComponentProps<typeof MultiSelect>["options"]
  queryPlaceholder: string
}

export const Filters: FunctionComponent<FiltersProps> = ({
  clearIconAriaLabel,
  label,
  multipleSelectedLabel,
  positionCategoryIdsLabel,
  positionCategories,
  queryPlaceholder,
}) => {
  const { navigateWithUpdatedQueryParams } = useJobRolesFilters()
  const searchParams = useSearchParams()
  const formRef = useRef<HTMLFormElement>(null)
  const { isSearchLoading } = useLayoutContext()

  function submitForm(): void {
    if (!formRef.current) {
      return
    }

    const formData = new FormData(formRef.current)
    const sanitizedQuery = ((formData.get("query") as string) || "").trim() || null

    navigateWithUpdatedQueryParams({
      query: sanitizedQuery,
    })
  }

  const onSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()
    submitForm()
  }

  const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Enter") {
      submitForm()
    }
  }

  const onFieldClearing = (): void => {
    setTimeout(() => {
      submitForm()
    })
  }

  const onFieldUpdate: FormEventHandler<HTMLInputElement> = (event): void => {
    if (event.currentTarget.type === "checkbox") {
      return submitForm()
    }

    const newValue = event.currentTarget.value.trim()
    const oldValue = (searchParams.get(event.currentTarget.name) || "").trim()
    const isFieldPristine = !newValue && !oldValue
    const isFieldUnchanged = newValue === oldValue

    if (isFieldPristine || isFieldUnchanged) {
      return undefined
    }

    return submitForm()
  }

  const onMultiSelectChange =
    (fieldName: string) =>
    (selectedValues: string[]): void => {
      navigateWithUpdatedQueryParams({
        [fieldName]: selectedValues.length ? selectedValues : null,
      })
    }

  return (
    <form className={styles.main} onSubmit={onSubmit} ref={formRef}>
      <div className={styles.mainBar}>
        <TextInput
          className={styles.primaryFilter}
          clearIconAriaLabel={clearIconAriaLabel}
          hasClearIcon
          id="query"
          inputClassName={styles.primaryFilterFieldInput}
          leftIcon={Search}
          name="query"
          onBlur={onFieldUpdate}
          onClear={onFieldClearing}
          onKeyDown={handleInputKeydown}
          placeholder={queryPlaceholder}
          readOnly={isSearchLoading}
          value={searchParams.get("query") || ""}
          aria-label={label}
        />
        <MultiSelect
          className={styles.primaryFilter}
          clearIconLabel={clearIconAriaLabel}
          defaultValue={searchParams.getAll("positionCategoryIds")}
          id="positionCategoryIds"
          aria-label={positionCategoryIdsLabel}
          multipleSelectedOptionsTextFormatter={amount => multipleSelectedLabel.replace("%{number}", `${amount}`)}
          name="positionCategoryIds"
          onSelect={onMultiSelectChange("positionCategoryIds")}
          options={positionCategories}
          placeholder={positionCategoryIdsLabel}
          readOnly={isSearchLoading}
        />
      </div>
    </form>
  )
}
